/**
 * bundle.schema.ts
 * ----------------
 * Zod schema for VolumeConfigInterface.
 *
 * This single schema is used for BOTH the base document and every variant
 * document – variants are identical shapes, just with different values.
 *
 * Only the fields that can be edited inside the bundle editor are validated
 * here. Fields like `form_id`, `bundle_type`, and internal metadata are
 * optional/passthrough.
 */

import { z } from "zod";
import { fontSize, hexColor, optionalNumber, optionalString, boolean, string, number } from "../../hooks/schemaHelpers";
import { BUNDLE_VISIBILITY_OPT } from "../../constants";

// ---------------------------------------------------------------------------
// Primitives / Enums
// ---------------------------------------------------------------------------

// Broad string – DiscountTypes in the existing codebase can be many values
const DiscountTypeSchema = z.string();
const WidgetShowOptionsSchema = z.enum(["show_everywhere", "show_only_when_targeting", "hide_everywhere"]);

// ---------------------------------------------------------------------------
// Offer
// ---------------------------------------------------------------------------
const OfferSchema = z.object({
    discount_type: DiscountTypeSchema,
    discount_value: z.any(),
    quantity: z
        .number({ message: "Quantity is required" })
        .int("Quantity must be a whole number")
        .min(1, "Quantity must be ≥ 1")
        .max(1999, "Quantity must be < 2000"),
    offer_title: z.string().min(1, "Offer title is required"),
    discount_label: z.string(),
    discount_label_selected: z.boolean(),
    discount_label_type: z.enum(["none", "custom", "ribbon", "ticket", "tag", "skull", "flag"]),
    discount_label_custom_img: z.string(),
    discount_label_custom_img_size: z.number(),
    discount_label_custom_img_radius: z.number(),
    discount_label_custom_img_fit: z.string(),
    tag: z.string(),
    tag_selected: z.boolean(),
    allow_free_gifts: z.boolean(),
    free_gifts: z.array(z.any()),
    free_gift_text: z.string(),
    allow_img: z.boolean(),
    img_url: z.string(),
    img_size: z.number(),
    img_radius: z.number(),
    img_fit: z.string(),
    allow_extra_items: z.boolean().optional(),
    show_quantity_selector: z.boolean(),
    free_gift_template: z.enum(["old", "modern"]),
    free_gift_show_price: z.boolean().optional(),
    offer_type: z.enum(["quantity_break", "product_bundle"]),
    bundle_products: z.array(z.any()),
    bundle_layout: z.enum(["vertical", "horizontal"]),
    allow_upsell_products: z.boolean(),
    upsell_products: z.array(z.any()),
});

// ---------------------------------------------------------------------------
// Design
// ---------------------------------------------------------------------------
const BundlesDesignSchema = z.object({
    default_cta: boolean(),
    bundle_title_size: fontSize("Bundle title font size", { min: 1, max: 72 }),
    bundle_title_color: hexColor("Bundle title"),
    bundle_title_style: string(),
    bundle_widget_bg: hexColor("Bundle widget"),
    bundle_widget_font_family: string(),
    product_name_size: fontSize("Product name font", { min: 1, max: 72 }),
    product_name_color: hexColor("Product name"),
    product_name_style: string(),
    product_price_size: fontSize("Product price font", { min: 1, max: 72 }),
    product_price_color: hexColor("Product price"),
    product_price_style: string(),
    variant_selector_text_size: fontSize("Variant Selector size", null, 12),
    variant_selector_text_color: hexColor("Variant Selector", "#383838"),
    variant_selector_text_style: optionalString("450 normal"),
    product_img_size: optionalString(),
    product_img_show: optionalString(),
    product_img_border_radius: optionalNumber(),
    border_color: hexColor("Product border"),
    border_width: fontSize("Border width font", { min: 0, max: 15 }),
    border_radius: number(),
    bundle_border_radius: number(),
    background_color: hexColor("Product background"),
    hover_effect: string(),
    hover_color: hexColor("Hover"),
    hover_intensity: number(),
    hover_size: fontSize("Hover size font", { min: 0, max: 100 }),
    elegant_badge_content: optionalString(),
    highlighted_offer_tag_size: fontSize("Offer tag font"),
    highlighted_offer_tag_color: hexColor("Highlighted offer tag"),
    highlighted_offer_tag_background_color: hexColor("Highlighted offer tag background"),
    highlighted_offer_tag_style: string(),
    highlighted_price_tag_size: fontSize("Price tag font"),
    highlighted_price_tag_color: hexColor("Highlighted price tag"),
    highlighted_price_tag_style: string(),
    total_footer_text_size: fontSize("Total footer text font"),
    total_footer_text_color: hexColor("Bottom bar text"),
    total_footer_text_style: string(),
    total_footer_price_text_size: fontSize("Total footer price font"),
    total_footer_price_text_color: hexColor("Total footer price text"),
    total_footer_price_text_style: string(),
    total_footer_background_color: hexColor("Bottom bar background"),
    total_footer_background_style: string(),
    product_background_color: hexColor("Product background"),
    product_border_color: hexColor("Product border"),
    add_to_cart_bg_color: hexColor("Add to cart background"),
    add_to_cart_hover_bg_color: hexColor("Add to cart hover background"),
    add_to_cart_border_radius: number(),
    add_to_cart_border_color: hexColor("Add to cart border"),
    add_to_cart_border_width: fontSize("Add to cart border width"),
    add_to_cart_text_color: hexColor("Add to cart text"),
    add_to_cart_text_size: fontSize("Add to cart text font"),
    add_to_cart_text_style: string(),
    free_gift_header_bg_color: hexColor("Free gift header background"),
    free_gift_header_color: hexColor("Free gift header text"),
    free_gift_header_size: fontSize("Free gift header text font"),
    free_gift_header_style: string(),
    free_gift_bg_color: hexColor("Free gift background"),
    free_gift_text_color: hexColor("Free gift text"),
    free_gift_text_size: fontSize("Free gift text font"),
    free_gift_text_style: string(),
    free_gift_border: boolean(),
    free_gift_border_width: fontSize("Free gift border width"),
    free_gift_border_color: hexColor("Free gift border"),
    free_gift_compare_price_color: hexColor("Free gift compare price"),
    free_gift_compare_price_size: fontSize("Free gift compare price font"),
    free_gift_compare_price_style: string(),
    upsell_bg_color: hexColor("Upsell background"),
    upsell_text_color: hexColor("Upsell text"),
    upsell_text_size: fontSize("Upsell text font"),
    upsell_text_style: string(),
    upsell_price_color: hexColor("Upsell price"),
    upsell_price_size: fontSize("Upsell price font"),
    upsell_price_style: string(),
    upsell_compare_price_color: hexColor("Upsell compare price"),
    upsell_compare_price_size: fontSize("Upsell compare price font"),
    upsell_compare_price_style: string(),
    quantity_selector_bg_color: hexColor("Quantity selector background"),
    quantity_selector_border_color: hexColor("Quantity selector border"),
    quantity_selector_border_width: fontSize("Quantity selector border width", { min: 0, max: 15 }),
    quantity_selector_border_radius: number(),
    quantity_selector_text_color: hexColor("Quantity selector text"),
    quantity_selector_text_size: fontSize("Quantity selector text font"),
    quantity_selector_text_style: string(),
    special_text_background_color: hexColor("Special text background"),
    special_text_color: hexColor("Special text"),
    special_text_font_size: fontSize("Special text font"),
    special_text_style: string(),
    discount_label_background_color: hexColor("Discount label background"),
    discount_label_color: hexColor("Discount label"),
    discount_label_font_size: fontSize("Discount label font"),
    discount_label_style: string(),
    discount_label_primary_color: hexColor("Discount label primary"),
    discount_label_secondary_color: hexColor("Discount label secondary"),
    discount_label_size: fontSize("Discount label size", { min: 0, max: 200 }),
    special_text_bg_opacity: boolean(),
    swatches_select_color: hexColor("Swatches select"),
    show_line_around_title_color: hexColor("Show line around title"),
    timer_color: hexColor("Timer"),
    timer_text_color: hexColor("Timer text"),
    timer_background_color: hexColor("Timer background"),
    timer_font_style: string(),
    timer_font_size: fontSize("Timer font"),
    savings_summary_text_color: hexColor("Savings summary text"),
    savings_summary_background_color: hexColor("Savings summary background"),
    savings_summary_font_size: fontSize("Savings summary font"),
    savings_summary_font_style: string(),
    savings_summary_border_radius: fontSize("Savings summary border radius", { min: 0, max: 100 }),
});

// ---------------------------------------------------------------------------
// Savings summary
// ---------------------------------------------------------------------------
const SavingsSummarySchema = z.object({
    status: z.boolean(),
    title: z.string(),
    icon_type: z.enum(["none", "checkmark", "dot", "arrow", "star", "heart", "thumbs_up", "custom"]),
    icon_url: z.string(),
    include_free_gift_value: z.boolean(),
    hide_when_no_savings: z.boolean(),
    text_align: z.enum(["left", "center", "right"]),
});

// ---------------------------------------------------------------------------
// Targeting
// ---------------------------------------------------------------------------
const TargetingSchema = z.object({
    // includeCustomers: z.enum(["all", "tag", "lifetimeSpent"]),
    // excludeCustomers: z.enum(["none", "tag", "lifetimeSpent"]),
    // includeCustomerData: z.string(),
    // excludeCustomerData: z.string(),
    // shopifyMarketsTargeting: z.enum(["all", "targeted"]),
    // shopifyMarketsTargetingData: z.array(z.string()),
    // includeLocations: z.array(z.string()),
    // excludeLocations: z.array(z.string()),
    targetingType: z.string(),
    customerTagsEnabled: z.boolean(),
    customerTagType: z.string(),
    customerTags: z.string(),
    customerSpentEnabled: z.boolean(),
    customerSpentType: z.string(),
    customerSpent: z.string(),
    customerCountriesEnabled: z.boolean(),
    customerCountryType: z.string(),
    customerCountries: z.array(z.string()),
});

// ---------------------------------------------------------------------------
// General settings
// ---------------------------------------------------------------------------
const GeneralSettingsSchema = z
    .object({
        show_bundle_on_product_page: z.boolean().optional(),
        show_multiple_bundles_on_product_page: z.boolean().optional(),
        redirect_after_adding_product_to_cart: z.string().optional(),
        redirect_to_checkout: z.boolean().optional(),
        show_powered_by_kefi: z.boolean().optional(),
        max_width: z.number().optional(),
        bundle_alignment: z.string().optional(),
        override_max_width: z.boolean().optional(),
        override_margin_top: z.boolean().optional(),
        margin_top: z.number().optional(),
    })
    .optional();

// ---------------------------------------------------------------------------
// Bundle settings
// ---------------------------------------------------------------------------
const BundleSettingsSchema = z
    .object({
        enable_stock_control: z.boolean(),
        out_of_stock_text: z.string().min(1, "Out of stock text is required"),
        listing_variant_with_image: z.boolean(),
        work_only_with_widget: z.boolean(),
        bundle_position: z.string(),
        custom_selector: z.string(),
        custom_css: z.string().optional(),
        custom_js: z.string().optional(),
    })
    .optional();

// ---------------------------------------------------------------------------
// Root bundle schema (used for both Base and Variants)
// ---------------------------------------------------------------------------
export const bundleSchema = z
    .object({
        // Metadata / identifiers (optional – not controlled by form)
        status: z.boolean(),
        bundle_type: z.string().optional(),
        form_id: z.string().optional(),
        is_paused: z.boolean().optional(),
        ai_bundle_status: z.boolean().optional(),
        // product_bundle_id: z.string().optional(),
        source: z.string().optional(),

        // Content
        bundle_name: z.string(),
        offer_name: z.string(),
        title: z.string().min(1, "Title is required"),
        description: z.string(),
        volume_product_selection: z.enum(["all", "specific", "exclude", "collection"]),
        bundle_visibility: z.nativeEnum(BUNDLE_VISIBILITY_OPT),
        bundle_product_level: z.enum(["product", "variant"]),
        hide_native_elements: z.string(),
        add_to_cart_cta_text: z.string().min(1, "Add to Cart text is required"),
        add_to_cart_cta_icon_type: z
            .enum(["none", "checkmark", "dot", "arrow", "star", "heart", "thumbs_up", "custom"])
            .optional(),
        add_to_cart_cta_icon_url: z.string().optional(),
        savings_summary: SavingsSummarySchema.optional(),
        quantity_text: z.string(),
        free_text: z.string().min(1, "Free text is required"),
        no_stock_text: z.string().min(1, "Out of stock text is required"),
        out_of_stock_disable_text: z.string().min(1, "Out of stock message is required"),
        show_unit_price: z.boolean(),
        show_unit_price_text: z.string().min(1, "Unit price text is required"),
        show_variant_selector: z.boolean(),
        show_line_around_title: z.boolean(),
        show_free_gift_popper: z.boolean(),
        use_compare_at_price: z.boolean(),
        apply_discount_on_compare_price: z.boolean(),
        enable_subscription_integration: z.boolean(),
        selected_template_volume: z.string(),
        show_swatches: z.boolean(),
        show_option_labels_once: z.boolean(),
        hide_item_numbers: z.boolean(),
        use_compact_spacing: z.boolean(),
        variant_label_position: z.enum(["above", "beside"]),
        swatchShape: z.enum(["circle", "rounded", "square"]),
        swatchSize: z.number(),
        swatchOptions: z.array(z.any()),
        swatchesProductsMap: z.record(z.string(), z.any()),
        default_offer: z.number(),
        defaultVariantTypes: z.array(z.any()),

        // Countdown timer
        show_countdown_timer: z.boolean(),
        countdown_timer_minutes: z.number().min(1),
        countdown_timer_text: z.string().min(1, "Timer text is required"),

        // Dates
        start_date: z.string(),
        end_date: z.string().nullable(),

        // Discounts toggles
        order_discounts: z.boolean(),
        product_discounts: z.boolean(),
        shipping_discounts: z.boolean(),
        free_shipping: z.boolean(),

        // Products (skip_keys – never variant-overridden)
        products: z.array(z.any()),
        collections: z.array(z.any()),
        discounted_products: z.array(z.any()),
        offered_products: z.array(z.any()).optional(),
        free_gifts: z.array(z.any()),
        bundle_products: z.array(z.any()),
        upsell_products: z.array(z.any()),

        // Offers
        volume_discounts: z
            .array(OfferSchema)
            .min(1, "At least one offer is required")
            .superRefine((offers, ctx) => {
                offerLoop: for (let i = 0; i < offers.length; i++) {
                    const offer = offers[i];
                    if (offer.allow_free_gifts && offer.free_gifts.length === 0) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `Offer ${i + 1}: Free gift product is required`,
                            path: [i, "free_gifts"],
                        });
                        continue offerLoop;
                    }
                    if (offer.allow_upsell_products && (!offer.upsell_products || offer.upsell_products.length === 0)) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `Offer ${i + 1}: Upsell product is required`,
                            path: [i, "upsell_products"],
                        });
                        continue offerLoop;
                    }
                    if (offer.allow_img && offer.img_url.length === 0) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `Offer ${i + 1}: Offer image is required`,
                            path: [i, "img_url"],
                        });
                        continue offerLoop;
                    }
                }
            }),

        // Design (skip_key – never variant-overridden)
        bundles_design: BundlesDesignSchema,

        // Targeting
        targeting: TargetingSchema,

        // Settings (skip_keys)
        general_settings: GeneralSettingsSchema,
        bundles_settings: BundleSettingsSchema,

        // Widget
        show_bundle_widget: WidgetShowOptionsSchema,
        animation_add_to_cart_btn: z.string(),
        max_width: z.number().optional(),
        SwatchesBundleConfigTouchedRef: z.any().optional(),
    })
    .superRefine((data, ctx) => {
        if (["specific", "exclude"].includes(data.volume_product_selection) && data.products.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["products"],
                message: "At least one product is required.",
            });
        }

        if (data.volume_product_selection === "collection" && data.collections.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["collections"],
                message: "At least one collection is required.",
            });
        }
    });

export type BundleFormValues = z.infer<typeof bundleSchema>;

// ---------------------------------------------------------------------------
// A/B variant schema – wraps the bundle schema
// ---------------------------------------------------------------------------
export const abVariantSchema = z.object({
    id: z.string(),
    // overrides is not stored in the form; it's generated on save
});

export type ABVariant = z.infer<typeof abVariantSchema> & {
    /** Generated on save from cache diff. Not part of form state. */
    overrides?: Partial<BundleFormValues>;
};
